import React, { useState , useEffect} from 'react'
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import './PackageTable.css'

const isOverflown = (element) => {
    return (
      element.scrollHeight > element.clientHeight ||
      element.scrollWidth > element.clientWidth
    );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
    const { width, value } = props;
    const wrapper = React.useRef(null);
    const cellDiv = React.useRef(null);
    const cellValue = React.useRef(null);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [showFullCell, setShowFullCell] = React.useState(false);
    const [showPopper, setShowPopper] = React.useState(false);
  
    const handleMouseEnter = () => {
      const isCurrentlyOverflown = isOverflown(cellValue.current);
      setShowPopper(isCurrentlyOverflown);
      setAnchorEl(cellDiv.current);
      setShowFullCell(true);
    };
  
    const handleMouseLeave = () => {
      setShowFullCell(false);
    };
  
    React.useEffect(() => {
      if (!showFullCell) {
        return undefined;
      }
  
      function handleKeyDown(nativeEvent) {
        // IE11, Edge (prior to using Bink?) use 'Esc'
        if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
          setShowFullCell(false);
        }
      }
  
      document.addEventListener('keydown', handleKeyDown);
  
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }, [setShowFullCell, showFullCell]);
  
    return (
      <Box
        ref={wrapper}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          zIndex:100,
          alignItems: 'center',
          lineHeight: '24px',
          width: 1,
          height: 1,
          position: 'relative',
          display: 'flex',
        }}
      >
        <Box
          ref={cellDiv}
          sx={{
            height: 1,
            width,
            display: 'block',
            position: 'absolute',
            top: 0,
          }}
        />
        <Box
          ref={cellValue}
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {value}
        </Box>
        {showPopper && (
          <Popper
            open={showFullCell && anchorEl !== null}
            anchorEl={anchorEl}
            style={{ width, marginLeft: -17 }}
          >
            <Paper
              elevation={1}
              style={{ minHeight: wrapper.current.offsetHeight - 3 }}
            >
              <Typography variant="body2" style={{ padding: 8 }}>
                {value}
              </Typography>
            </Paper>
          </Popper>
        )}
      </Box>
    );
  });

GridCellExpand.propTypes = {
value: PropTypes.string.isRequired,
width: PropTypes.number.isRequired,
};
  

function renderCellExpand(params) {
return (
    <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />
    );
}

renderCellExpand.propTypes = {
    /**
     * The column of the row that the current cell belongs to.
     */
    colDef: PropTypes.object.isRequired,
    /**
     * The cell value, but if the column has valueGetter, use getValue.
     */
    value: PropTypes.string.isRequired,
};

const columns = [
{ field: 'package_name', headerName: 'Package Name', headerAlign: 'center', headerClassName: 'header-text', flex: 0.5, renderCell: renderCellExpand},
{ field: 'subtest_mapping', headerName: 'Sub Tests', headerAlign: 'center', headerClassName: 'header-text', flex: 1, renderCell: renderCellExpand},
{field: 'relatedEntity', headerName: 'Related Entity', headerAlign: 'center', headerClassName: 'header-text', flex: 0.25, renderCell: renderCellExpand}
];

export default function PackageTable({tableData, setSelectedRows}) {
    const [selectionModel, setSelectionModel] = useState([]);
    return (
        <div style={{height: '43rem', margin: '30px'}}>
        <DataGrid
            rows={tableData}
            getRowId={(row) => row.package_name}
            columns={columns}
            pageSize={15}
            rowsPerPageOptions={[15]}
            checkboxSelection
            selectionModel={selectionModel}
            hideFooterSelectedRowCount
            components={{Toolbar: GridToolbar}}
            sx={{ border: 1,
              '& .MuiDataGrid-cell:hover': {
                  cursor: 'pointer',
              },
              '& .MuiDataGrid-cell': {
                  borderRight: 0.5,
                  borderTop: 0.5,
                  justifyContent: 'center',
              },
              "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#3275a8",
                  color: "white",
                  fontSize: 16,
              },
              "&.MuiDataGrid-root .MuiDataGrid-columnHeaderCheckbox":{
                  visibility: 'hidden'
              },
              
            }}
            componentsProps={{toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            onSelectionModelChange={(selection) => {
                if(selection.length > 1) {
                    selection.shift()
                }
                const selected = tableData.filter((row) =>
                    selection[0] === row.package_name
                );
                setSelectedRows(selected);
                if (selection.length > 1) {
                    const selectionSet = new Set(selectionModel);
                    const result = selection.filter((s) => !selectionSet.has(s));
                    setSelectionModel(result);
                    } 
                else {
                    setSelectionModel(selection);
                }
            }}
        />
        </div>
    );
}

